import { type LucideIcon, MoreHorizontal } from "lucide-react"
import { Fragment, type ReactElement, useRef } from "react"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu"

export interface RowAction {
	label: string
	icon?: LucideIcon
	destructive?: boolean
	/** Черта перед пунктом: отделить опасные действия. */
	separatorBefore?: boolean
	hidden?: boolean
	onSelect: () => void
}

interface RowActionsMenuProps {
	/** Подпись кнопки для экранного диктора: «Действия с …». */
	label: string
	actions: RowAction[]
	/** Своя кнопка вместо «⋯». */
	trigger?: ReactElement
	align?: "start" | "center" | "end"
	contentClassName?: string
}

/**
 * Меню «⋯» у строки. Немодальное, а действие запускается только после закрытия меню:
 * модальное меню Radix, открывшее диалог, оставляло на body pointer-events: none,
 * и страница переставала отвечать на клики.
 */
export function RowActionsMenu({
	label,
	actions,
	trigger,
	align = "end",
	contentClassName,
}: RowActionsMenuProps) {
	const pending = useRef<(() => void) | null>(null)
	const visible = actions.filter((action) => !action.hidden)
	if (visible.length === 0) return null

	return (
		<DropdownMenu modal={false}>
			<DropdownMenuTrigger asChild>
				{trigger ?? (
					<Button variant="ghost" size="icon" aria-label={label}>
						<MoreHorizontal />
					</Button>
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align={align}
				className={cn("min-w-48", contentClassName)}
				onCloseAutoFocus={(event) => {
					const run = pending.current
					if (!run) return
					pending.current = null
					// Фокус уйдёт в открывшийся диалог, а не обратно на «⋯».
					event.preventDefault()
					run()
				}}
			>
				{visible.map((action, index) => {
					const Icon = action.icon
					return (
						<Fragment key={action.label}>
							{action.separatorBefore && index > 0 ? <DropdownMenuSeparator /> : null}
							<DropdownMenuItem
								className={cn(action.destructive && "text-red-400 focus:text-red-300")}
								onSelect={() => {
									pending.current = action.onSelect
								}}
							>
								{Icon ? <Icon className="text-muted-foreground" /> : null}
								{action.label}
							</DropdownMenuItem>
						</Fragment>
					)
				})}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
